import { useEffect } from 'react';

const POLL_INTERVAL_MS = 1500;

/**
 * Poll a status-bearing job (export/import) every `POLL_INTERVAL_MS` while
 * it's pending/processing; stops automatically once it settles or the
 * component unmounts. Shared by ImportExportDialog.tsx and any other UI
 * that kicks off a queued export/import job (doc 09 §2-3) and needs to
 * reflect its progress without the caller managing timers by hand.
 */
export function useJobPolling<T extends { status: string }>(
    job: T | null,
    fetchJob: () => Promise<T>,
    onUpdate: (job: T) => void,
) {
    useEffect(() => {
        if (!job || job.status === 'completed' || job.status === 'failed') return;

        const timer = setInterval(async () => {
            try {
                onUpdate(await fetchJob());
            } catch {
                // Transient poll failure — next tick will retry.
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [job, fetchJob, onUpdate]);
}
